import { Modal, message } from "antd";
import { useSelector } from "react-redux";
import { useState } from "react";
import "./shareModal.scss";
import facebookIcon from "../../../assets/icons/facebook-logo.svg";
import twitterIcon from "../../../assets/icons/twitter.svg";
import instagramIcon from "../../../assets/icons/instagram.svg";
import starIcon from "../../../assets/icons/star-blue.svg";
import { shareCertificateRequest } from "../../../utils/apis/account";

const ShareModal = ({ isOpen, onClose, certificateUrl, certificateTitle, certificateId }) => {
    const idToken = useSelector((state) => state.auth.idToken);
    const [loadingPlatform, setLoadingPlatform] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);

    // Get share URL from the backend API
    const getShareUrl = async () => {
        let shareUrlToUse = certificateUrl;
        if (certificateId) {
            try {
                const response = await shareCertificateRequest(idToken, certificateId);
                if (response && response.url) {
                    shareUrlToUse = response.url;
                }
            } catch (error) {
                console.error(error);
            }
        }
        return shareUrlToUse;
    };

    // Copy certificate link to clipboard
    const handleCopyLink = async () => {
        try {
            const shareUrl = await getShareUrl();
            await navigator.clipboard.writeText(shareUrl);
            setLinkCopied(true);
            message.success("Certificate link copied to clipboard!");
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (e) {
            message.error("Failed to copy link");
        }
    };

    // Download handler - simple direct download
    const handleDownload = () => {
        // Create a temporary link and trigger download
        const a = document.createElement("a");
        a.href = certificateUrl;
        a.download = `${certificateTitle || "Certificate"}.png`;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleShare = async (platform) => {
        setLoadingPlatform(platform);

        const shareUrlToUse = await getShareUrl();
        const shareText = encodeURIComponent(`Check out my ${certificateTitle} certificate!`);
        const shareUrl = encodeURIComponent(shareUrlToUse);

        switch (platform) {
            case "twitter":
                // Open Twitter and trigger download
                window.open(
                    `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
                    "_blank",
                    "width=600,height=500"
                );
                // Trigger download after a short delay
                setTimeout(() => {
                    handleDownload();
                    message.info("Image download started! Attach it to your tweet.");
                }, 500);
                break;

            case "facebook":
                // Open Facebook and trigger download
                window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
                    "_blank",
                    "width=600,height=500"
                );
                setTimeout(() => {
                    handleDownload();
                    message.info("Image download started! Attach it to your post.");
                }, 500);
                break;

            case "instagram":
                // For Instagram, just download and copy link
                handleDownload();
                try {
                    await navigator.clipboard.writeText(shareUrlToUse);
                    message.success("Image download started & link copied! Share on Instagram.");
                } catch (e) {
                    message.info("Image download started! Share on Instagram.");
                }
                break;

            default:
                break;
        }

        setLoadingPlatform(null);
        onClose();
    };

    const platforms = [
        {
            name: "X (Twitter)",
            key: "twitter",
            icon: twitterIcon,
            color: "#000",
        },
        // {
        //     name: "Instagram",
        //     key: "instagram",
        //     icon: instagramIcon,
        //     gradient: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
        // },
        {
            name: "Facebook",
            key: "facebook",
            icon: facebookIcon,
            color: "#1877F2",
        },
        {
            name: "Testimonials",
            key: "testimonials",
            icon: starIcon,
            color: "#36d66b",
            disabled: true,
        },
    ];

    return (
        <Modal
            open={isOpen}
            onCancel={() => {
                setLinkCopied(false);
                setLoadingPlatform(null);
                onClose();
            }}
            footer={null}
            centered
            className="share-certificate-modal"
            width={440}
            style={{
                maxWidth: "calc(100vw - 32px)",
                top: 0
            }}
            styles={{
                body: { padding: 0 }
            }}
        >
            <div className="share-modal-content">
                <h2 className="share-modal-title">Share Certificate</h2>
                <p className="share-modal-subtitle">Share your achievement on social media</p>

                {/* Certificate image preview */}
                <div className="share-certificate-preview">
                    <img src={certificateUrl} alt="Certificate preview" />
                </div>

                {/* Action buttons */}
                <div className="share-action-buttons">
                    <button
                        className={`share-copy-link-btn ${linkCopied ? "copied" : ""}`}
                        onClick={handleCopyLink}
                    >
                        {linkCopied ? "✓ Copied!" : "📋 Copy Link"}
                    </button>
                    <button className="share-download-btn" onClick={handleDownload}>
                        ⬇ Download Image
                    </button>
                </div>

                <p className="share-helper-text">
                    Download the image and upload it when sharing for best results
                </p>

                <div className="share-platforms">
                    {platforms.map((platform) => (
                        <div
                            key={platform.key}
                            className={`share-platform-item ${platform.disabled || loadingPlatform === platform.key ? "disabled" : ""}`}
                            onClick={() =>
                                !platform.disabled && loadingPlatform !== platform.key && handleShare(platform.key)
                            }
                        >
                            <div
                                className="platform-icon"
                                style={{ background: platform.gradient || platform.color }}
                            >
                                <img src={platform.icon} alt={platform.name} />
                            </div>
                            <span className="platform-name">
                                {loadingPlatform === platform.key ? "Sharing..." : platform.name}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export default ShareModal;
